import { FC, ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { EIconSize } from 'utils/constants/app/ui';
import {
  competitions,
  regions,
  sports
} from 'utils/constants/sportsbook/data-fields';
import {
  RACING_SPORT_ALIAS,
  RACING_SPORT_IDS
} from 'utils/constants/sportsbook/racing-sport';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { sbRedirect, shouldRedirect } from 'utils/generic/route-manipulation';
import { showToastError } from 'utils/generic/show-toast-error';
import { isMobile } from 'utils/is-mobile';
import RidGenerator from 'utils/swarm/rid-generator';
import { serializeSwarmData } from 'utils/swarm/serialize-swarm-data';
import { ERacingPageTypes } from 'interfaces/racing';
import { Sport } from 'interfaces/sportsbook-data-levels';
import { executeCommand } from 'services/get-data-level';
import { Loading } from 'components/Loading';
import { setTimeFilterData, setTournamentSportIds } from 'store/actions';
import { setMustClose } from 'store/actions/betslip';
import { setGames, setRegionCompData } from 'store/actions/new-sportsbook';
import {
  getTimeFiltersData,
  getTournamentSportIds
} from 'store/selectors/sport-data';
import './index.less';

export const Container = styled.span`
  position: relative;
  overflow: hidden;
  min-height: 10px;
  display: flex;
  align-items: center;
  cursor: pointer;

  & > span {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    line-height: 16px;
  }
`;

type Props = {
  gameName: string | ReactElement[];
  gameId: string;
};

export const GameNameRedirection: FC<Props> = ({ gameId, gameName }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();

  const timeStamp = useSelector(getTimeFiltersData);
  const tournamentSportIds = useSelector(getTournamentSportIds);

  const getGameData = () => {
    if (gameId) {
      setIsLoading(true);
      executeCommand(
        RidGenerator.gForCommand('CheckGameForRedirect'),
        {
          ...sports,
          ...regions,
          ...competitions,
          game: ['type']
        },
        { game: { id: Number(gameId) } },
        (data: { data: { sport: Record<string, Sport> } }) => {
          setIsLoading(false);
          const serializedData = serializeSwarmData(data.data);

          if (serializedData?.games?.length) {
            const isEsport =
              serializedData.sports[0].type === 0 &&
              SpringConfigs.PAGE_URLS.esport;

            const isLive = serializedData.games[0].type === 1;
            const isRacing = RACING_SPORT_IDS.includes(
              Number(serializedData.sports[0].id)
            );

            const redirectBase = isEsport
              ? SpringConfigs.PAGE_URLS.esport
              : isLive
              ? SpringConfigs.PAGE_URLS.live
              : isRacing
              ? SpringConfigs.PAGE_URLS.racing
              : SpringConfigs.PAGE_URLS.prematch;

            const isLongPath = !isEsport || !isMobile();
            const alias = `/${`${SpringConfigs.SPORTSBOOK_MOUNT_PATH}/`}${
              isRacing
                ? `${ERacingPageTypes.UPCOMING}/${Object.keys(
                    RACING_SPORT_ALIAS
                  ).find(
                    key =>
                      RACING_SPORT_ALIAS[key] === serializedData.sports[0].id
                  )}`
                : serializedData.sports[0]['alias']
            }/${
              isLongPath
                ? isRacing
                  ? ''
                  : `${serializedData.regions[0].alias}/`
                : ''
            }${
              isLongPath ? `${serializedData.competitions[0].id}/` : ''
            }${gameId}${
              isEsport ? `?type=${isLive ? 'live' : 'prematch'}` : ''
            }`;

            const finalPath = `${redirectBase}${alias}`.replace(
              /([^:]\/)\/+/g,
              '$1'
            );

            if (
              finalPath === window.location.href ||
              finalPath === window.location.pathname + window.location.search
            ) {
              dispatch(setMustClose(true));
            } else {
              if (!shouldRedirect(redirectBase)) {
                const pathnameBase =
                  redirectBase.split(`${window.location.origin}`)[1] || '';

                if (timeStamp && Object.keys(timeStamp).length) {
                  dispatch(
                    setTimeFilterData({
                      option: 'All',
                      date: false,
                      count: undefined,
                      name: t('sportsbook.allEvents')
                    })
                  );

                  if (!isMobile()) {
                    dispatch(setRegionCompData(null, true));
                    dispatch(setGames(null));
                  }
                }

                if (
                  !tournamentSportIds?.includes(
                    serializedData.sports[0]?.id || null
                  )
                ) {
                  dispatch(setTournamentSportIds(null));
                }

                history.push(pathnameBase + alias);
                dispatch(setMustClose(true));
              } else {
                if (window.fromNext) {
                  const pathnameBase =
                    redirectBase.split(`${window.location.origin}`)[1] || '';

                  window.nextRouter.push(pathnameBase + alias);
                } else {
                  sbRedirect(finalPath, false, true);
                }
              }
            }
          } else {
            const errorKey = t('account.gameIsNotAvailable');
            showToastError(errorKey);
          }
        },
        false
      );
    }
  };

  if (!gameName) {
    return null;
  }

  return (
    <Container
      onClick={getGameData}
      title={typeof gameName === 'string' ? gameName : ''}
    >
      <span data-testid="betslip-game-teams">
        {typeof gameName === 'string' ? (
          gameName
        ) : (
          <>
            {gameName.map(elem => {
              return elem;
            })}
          </>
        )}
      </span>
      {isLoading && (
        <Loading
          size={EIconSize._16}
          className="gameNameRedirection__loading"
        />
      )}
    </Container>
  );
};
